import type { MailSearchSort, ServerEvent } from '@email/shared';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

export type MailScope = 'unified' | 'snoozed' | { accountId: number; folderId: number };
export type RemoteImageRequestMode = 'blocked' | 'standard' | 'compatibility' | 'direct';

export function mailScopeKey(scope: MailScope): string {
  return typeof scope === 'string' ? scope : `${scope.accountId}:${scope.folderId}`;
}

export const appKeys = {
  auth: ['auth'] as const,
  users: ['users'] as const,
  invites: ['invites'] as const,
  inviteCheck: (token: string) => ['invite-check', token] as const,
  version: ['version'] as const,
};

export const settingsKeys = {
  preferences: ['preferences'] as const,
  preference: (userId: number) => ['preferences', userId] as const,
  automationTokens: ['automation', 'tokens'] as const,
  automationActivity: ['automation', 'activity'] as const,
  aiProviders: ['ai', 'providers'] as const,
  aiActivity: ['ai', 'activity'] as const,
  notifications: ['notifications'] as const,
  mailRules: ['mail-rules'] as const,
  mailTemplates: ['mail-templates'] as const,
  mailRuleHistory: (ruleId: number) => ['mail-rules', ruleId, 'history'] as const,
  storageCapacity: ['storage', 'capacity'] as const,
  mailRecoverySettings: ['mail', 'recovery', 'settings'] as const,
};

export const mailKeys = {
  accounts: ['accounts'] as const,
  drafts: ['drafts'] as const,
  folders: ['folders'] as const,
  navigationCounts: ['mail', 'navigation-counts'] as const,
  syncStatus: ['sync-status'] as const,
  lists: ['mail', 'list'] as const,
  list: (scope: MailScope, bucket: string) =>
    ['mail', 'list', mailScopeKey(scope), bucket] as const,
  searches: ['mail', 'search'] as const,
  search: (query: string, sort: MailSearchSort) =>
    ['mail', 'search', query, sort] as const,
  threads: ['mail', 'thread'] as const,
  thread: (id: number) => ['mail', 'thread', id] as const,
  bodies: ['mail', 'body'] as const,
  messageBodies: (id: number) => ['mail', 'body', id] as const,
  body: (id: number, images: RemoteImageRequestMode) =>
    ['mail', 'body', id, images] as const,
  recovery: ['mail', 'recovery'] as const,
  recoveryList: (query: string) => ['mail', 'recovery', 'list', query] as const,
  recoveryDetails: ['mail', 'recovery', 'detail'] as const,
  recoveryDetail: (id: number) => ['mail', 'recovery', 'detail', id] as const,
};

export const calendarKeys = {
  state: ['calendar', 'state'] as const,
  events: ['calendar', 'events'] as const,
  eventRange: (start: number, end: number) =>
    ['calendar', 'events', start, end] as const,
  eventDetails: ['calendar', 'event'] as const,
  event: (calendarObjectId: number, recurrenceId: string | null) =>
    ['calendar', 'event', calendarObjectId, recurrenceId] as const,
  searches: ['calendar', 'search'] as const,
  search: (query: string) => ['calendar', 'search', query] as const,
  googleOAuth: ['calendar', 'google-oauth'] as const,
};

export const contactsKeys = {
  root: ['contacts'] as const,
  detail: (id: number) => ['contacts', 'detail', id] as const,
  sources: ['contacts', 'sources'] as const,
  list: (
    query: string,
    addressBookId: number | null,
    groupId: number | null,
  ) => ['contacts', 'list', query, addressBookId, groupId] as const,
  suggestionRoot: ['contacts', 'suggestions'] as const,
  suggestions: (query: string) =>
    ['contacts', 'suggestions', query] as const,
  duplicates: ['contacts', 'duplicates'] as const,
  discovery: (
    query: string,
    status: 'active' | 'dismissed',
    filter: 'recommended' | 'all',
    sort: 'relevance' | 'frequent' | 'recent',
  ) => ['contacts', 'discovery', query, status, filter, sort] as const,
};

export const plannerKeys = {
  root: ['planner'] as const,
  taskList: (
    query: string,
    status: 'open' | 'completed' | 'all',
    priority: string | null,
    view: string,
    dayStart: number,
  ) => ['planner', 'tasks', query, status, priority, view, dayStart] as const,
  task: (id: number) => ['planner', 'task', id] as const,
  note: (id: number) => ['planner', 'note', id] as const,
  reminders: ['planner', 'reminders'] as const,
  calendarTasks: (start: number, end: number) =>
    ['planner', 'calendar', start, end] as const,
  backlinks: (kind: string, id: number) =>
    ['planner', 'backlinks', kind, id] as const,
  noteList: (
    query: string,
    pinned: 'all' | 'pinned',
    tag: string | null,
  ) => ['planner', 'notes', query, pinned, tag] as const,
};

export interface QueryInvalidation<Args extends unknown[] = []> {
  queryKeys: (...args: Args) => readonly QueryKey[];
  invalidate: (queryClient: QueryClient, ...args: Args) => Promise<void>;
}

function defineInvalidation<Args extends unknown[]>(
  queryKeys: (...args: Args) => readonly QueryKey[],
): QueryInvalidation<Args> {
  return {
    queryKeys,
    invalidate: (queryClient, ...args) =>
      invalidateQueryKeys(queryClient, queryKeys(...args)),
  };
}

export async function invalidateQueryKeys(
  queryClient: QueryClient,
  queryKeys: readonly QueryKey[],
): Promise<void> {
  await Promise.all(
    queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

export const appInvalidations = {
  auth: defineInvalidation(() => [appKeys.auth]),
  users: defineInvalidation(() => [appKeys.users]),
  invites: defineInvalidation(() => [appKeys.invites]),
};

export const settingsInvalidations = {
  mailTemplates: defineInvalidation(() => [settingsKeys.mailTemplates]),
  preferences: defineInvalidation(() => [settingsKeys.preferences]),
  automationTokens: defineInvalidation(() => [settingsKeys.automationTokens]),
  automation: defineInvalidation(() => [
    settingsKeys.automationTokens,
    settingsKeys.automationActivity,
  ]),
  aiProviders: defineInvalidation(() => [settingsKeys.aiProviders]),
  aiProvidersAndActivity: defineInvalidation(() => [
    settingsKeys.aiProviders,
    settingsKeys.aiActivity,
  ]),
  aiActivity: defineInvalidation(() => [settingsKeys.aiActivity]),
  notifications: defineInvalidation(() => [settingsKeys.notifications]),
  mailRules: defineInvalidation(() => [settingsKeys.mailRules]),
  storageCapacity: defineInvalidation(() => [settingsKeys.storageCapacity]),
  mailRecovery: defineInvalidation(() => [
    settingsKeys.mailRecoverySettings,
    settingsKeys.storageCapacity,
    mailKeys.recovery,
    mailKeys.navigationCounts,
  ]),
};

export const mailInvalidations = {
  senderImages: defineInvalidation(() => [mailKeys.bodies]),
  drafts: defineInvalidation(() => [mailKeys.drafts]),
  draftsAndSync: defineInvalidation(() => [
    mailKeys.drafts,
    mailKeys.syncStatus,
  ]),
  accounts: defineInvalidation(() => [
    mailKeys.accounts,
    mailKeys.folders,
    mailKeys.navigationCounts,
    mailKeys.syncStatus,
  ]),
  accountEvent: defineInvalidation(() => [
    mailKeys.accounts,
    mailKeys.folders,
    mailKeys.navigationCounts,
    mailKeys.syncStatus,
    mailKeys.searches,
    settingsKeys.storageCapacity,
  ]),
  folderCounts: defineInvalidation(() => [
    mailKeys.folders,
    mailKeys.navigationCounts,
  ]),
  syncOverview: defineInvalidation(() => [
    mailKeys.accounts,
    mailKeys.syncStatus,
    settingsKeys.storageCapacity,
  ]),
  syncStatus: defineInvalidation(() => [mailKeys.syncStatus]),
  activity: defineInvalidation(() => [
    mailKeys.lists,
    mailKeys.searches,
    mailKeys.navigationCounts,
  ]),
  newMessage: defineInvalidation(() => [
    mailKeys.lists,
    mailKeys.searches,
    mailKeys.folders,
    mailKeys.navigationCounts,
  ]),
  thread: defineInvalidation((threadId: number) => [
    mailKeys.thread(threadId),
    mailKeys.lists,
    mailKeys.searches,
    mailKeys.navigationCounts,
  ]),
  messageBody: defineInvalidation((messageId: number) => [
    mailKeys.messageBodies(messageId),
    mailKeys.threads,
    mailKeys.searches,
  ]),
  actions: defineInvalidation(() => [
    mailKeys.lists,
    mailKeys.searches,
    mailKeys.threads,
    mailKeys.folders,
    mailKeys.navigationCounts,
    mailKeys.recovery,
    settingsKeys.mailRecoverySettings,
    settingsKeys.storageCapacity,
  ]),
  actionsAndSync: defineInvalidation(() => [
    mailKeys.lists,
    mailKeys.searches,
    mailKeys.threads,
    mailKeys.folders,
    mailKeys.navigationCounts,
    mailKeys.syncStatus,
  ]),
};

export const calendarInvalidations = {
  all: defineInvalidation(() => [
    calendarKeys.state,
    calendarKeys.events,
    calendarKeys.eventDetails,
    calendarKeys.searches,
    settingsKeys.storageCapacity,
  ]),
  state: defineInvalidation(() => [calendarKeys.state]),
  collections: defineInvalidation(() => [
    calendarKeys.state,
    calendarKeys.events,
    calendarKeys.searches,
  ]),
};

export const contactsInvalidations = {
  all: defineInvalidation(() => [
    contactsKeys.root,
    contactsKeys.suggestionRoot,
    settingsKeys.storageCapacity,
  ]),
};

export const plannerInvalidations = {
  all: defineInvalidation(() => [plannerKeys.root, settingsKeys.storageCapacity]),
  withCalendar: defineInvalidation(() => [
    plannerKeys.root,
    calendarKeys.state,
    settingsKeys.storageCapacity,
  ]),
};

/**
 * Resolve every SSE event through the same policies used by mutations.
 * Empty results are intentional: sync.progress updates the live progress store.
 */
export function serverEventInvalidationKeys(
  event: ServerEvent,
): readonly QueryKey[] {
  switch (event.type) {
    case 'preferences.updated':
      return settingsInvalidations.preferences.queryKeys();
    case 'sender_images.updated':
      return mailInvalidations.senderImages.queryKeys();
    case 'mail_rules.updated':
      return settingsInvalidations.mailRules.queryKeys();
    case 'mail_templates.updated':
      return settingsInvalidations.mailTemplates.queryKeys();
    case 'mail_recovery.updated':
      return settingsInvalidations.mailRecovery.queryKeys();
    case 'mail_snooze.updated':
      return mailInvalidations.actions.queryKeys();
    case 'automation.updated':
    case 'automation.activity':
      return settingsInvalidations.automation.queryKeys();
    case 'ai.updated':
      return settingsInvalidations.aiProviders.queryKeys();
    case 'ai.activity':
      return settingsInvalidations.aiActivity.queryKeys();
    case 'calendar.updated':
      return calendarInvalidations.all.queryKeys();
    case 'contacts.updated':
      return contactsInvalidations.all.queryKeys();
    case 'planner.updated':
      return plannerInvalidations.all.queryKeys();
    case 'notifications.updated':
      return settingsInvalidations.notifications.queryKeys();
    case 'draft.updated':
      return mailInvalidations.draftsAndSync.queryKeys();
    case 'account.updated':
      return mailInvalidations.accountEvent.queryKeys();
    case 'folder.counts':
      return mailInvalidations.folderCounts.queryKeys();
    case 'sync.status':
      return mailInvalidations.syncOverview.queryKeys();
    case 'sync.progress':
      return [];
    case 'mail.updated':
      return mailInvalidations.activity.queryKeys();
    case 'message.new':
      return mailInvalidations.newMessage.queryKeys();
    case 'thread.updated':
    case 'message.flags':
      return mailInvalidations.thread.queryKeys(event.threadId);
    case 'message.body_ready':
      return mailInvalidations.messageBody.queryKeys(event.messageId);
    case 'op.failed':
      return mailInvalidations.actionsAndSync.queryKeys();
    case 'op.updated':
      return mailInvalidations.syncStatus.queryKeys();
    case 'outbox.updated':
      return event.state === 'failed'
        ? mailInvalidations.draftsAndSync.queryKeys()
        : mailInvalidations.syncStatus.queryKeys();
  }
}

export function invalidateServerEvent(
  queryClient: QueryClient,
  event: ServerEvent,
): Promise<void> {
  return invalidateQueryKeys(queryClient, serverEventInvalidationKeys(event));
}
